import { useQuery } from '@tanstack/react-query';
import _, { camelCase } from 'lodash';
import { useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { FieldSectionType } from 'api/field-sections/types';
import { AccountFields, FieldsAPI } from 'api/fields/fields';
import { ObjectType } from 'api/pushes';
import {
  Field,
  FieldOption,
  FieldValue,
  FieldValueType,
  StatusField,
  StatusFieldOption,
} from 'api/tasks';
import { FieldType, SourceType } from 'api/tasks/field-meta';
import { useEpicFieldsQuery, useSprintFieldsQuery } from 'queries';
import { useAccountConfigQuery } from 'queries/account';
import { useTaskFieldsBaseQuery } from 'queries/fields';
import { FieldsStore } from 'stores/fields';
import { byFieldName } from 'utils/by-field-name';
import { RootState } from '../../index';

const selectFields = ({ taskFields }: RootState): AccountFields => taskFields;

const fieldsStore = ({ fields }: RootState): FieldsStore => fields;

const fieldNamesMaponDemand: Record<string, Field> = {};

/**
 *
 * @deprecated Use `FieldsStore` instead
 */
const useAccountFields = (): AccountFields =>
  useSelector(selectFields, shallowEqual);

const useFieldsStore = (): FieldsStore =>
  useSelector(fieldsStore, shallowEqual);

export interface TaskFieldsByProject {
  defaultFields: Array<Field>;
  projectFields: Array<Field>;
}

export const useFetchFieldByName = (
  fieldName: string,
  objectType: ObjectType,
) => {
  const query = useQuery({
    queryKey: ['field', fieldName, objectType],
    queryFn: async () => {
      const resp = await FieldsAPI.getPaginatedFields(objectType, {
        includedFieldNames: [fieldName],
        limit: 1,
      });

      return resp.data[0];
    },
  });

  return query;
};
export const useFetchMultipleFieldsByName = (
  fieldNames: string[],
  objectType: ObjectType,
) => {
  const query = useQuery({
    queryKey: ['field', ...(fieldNames ?? []), objectType],
    queryFn: async () => {
      const resp = await FieldsAPI.getPaginatedFields(objectType, {
        includedFieldNames: fieldNames,
        limit: fieldNames.length,
      });

      return resp.data;
    },
    enabled: fieldNames.length > 0,
  });

  return query;
};

const useBillableCategoryEffortField = () => {
  const { data: fields } = useFetchMultipleFieldsByName(
    [
      'defaultTimeEntryBillable',
      'defaultTimeEntryCategory',
      'effort',
      'priority',
    ],
    ObjectType.Task,
  );

  return _.keyBy(fields ?? [], (e) => camelCase(e.fieldName));
};

/**
 *
 * @deprecated Use `FieldsStore` instead
 *
 */
export const useAccountField = (fieldName: string): Field | undefined => {
  const { fields } = useAccountFields();

  return fields.find((f) => byFieldName(f, fieldName));
};

/**
 * Using `FieldsStore.getTaskFieldByName`
 */
export const useTaskField = (fieldName: string): Field | undefined => {
  const store = useFieldsStore();
  const fieldStore = useMemo(
    () => FieldsStore.getTaskFieldByName(fieldName)(store) as any,
    [store, fieldName],
  );

  return fieldStore[1];
};

const fieldNameLookup = {};

export const useTaskFieldV2 = (fieldName: string): Field | undefined => {
  const fieldsData = useTaskFieldsBaseQuery();

  return useMemo(() => {
    const name = fieldName?.toLowerCase();

    if (fieldNameLookup[name]) {
      return fieldNameLookup[name];
    }

    const f = fieldsData?.data?.find(
      (field) => field.fieldName?.toLowerCase() === name,
    );

    if (f) {
      fieldNameLookup[name] = f;
    }

    return f;
  }, [fieldsData, fieldName]);
};

export const PRIORITY_FIELD_NAME = 'priority';

const useBCPByName = (fieldName: string): Field | undefined => {
  const billableCategoryEffortField = useBillableCategoryEffortField();

  const field = useMemo(() => {
    if (fieldNamesMaponDemand[fieldName]) {
      return fieldNamesMaponDemand[fieldName];
    }

    if (billableCategoryEffortField) {
      const f = billableCategoryEffortField[fieldName];

      if (f) {
        fieldNamesMaponDemand[fieldName] = f;

        return f;
      }
    }

    return undefined;
  }, [billableCategoryEffortField, fieldName]);

  return field;
};

export const usePriorityField = (): Field | undefined => {
  return useBCPByName(PRIORITY_FIELD_NAME);
};

const EFFORT_FIELD_NAME = 'effort';

export const useEffortField = (): Field | undefined => {
  return useBCPByName(EFFORT_FIELD_NAME);
};

const CATEGORY_FIELD_NAME = 'defaultTimeEntryCategory';

export const useCategoryField = (): Field | undefined => {
  return useBCPByName(CATEGORY_FIELD_NAME);
};

const BILLABLE_FIELD_NAME = 'defaultTimeEntryBillable';

export const useBillableField = (): Field | undefined => {
  return useBCPByName(BILLABLE_FIELD_NAME);
};

export const usePriorityFieldOption = (
  value: number,
): FieldOption<number> | undefined => {
  const priority = usePriorityField();

  return (
    priority &&
    (priority?.metaData?.options ?? []).find((o) => o.value === value)
  );
};

export const useSelectedPriority = (
  fields: FieldValue<FieldValueType>[],
): FieldOption<number> | undefined => {
  const priority = usePriorityField();
  const field = fields.find(
    (f) => f.fieldId === priority?.fieldId,
  ) as FieldValue<number>;

  return usePriorityFieldOption(field?.fieldValue);
};

export const STATUS_FIELD_NAME = 'Status';
export const STATUS_FIELD_QUERY_KEY = 'STATUS_FIELD';

export const useStatusFieldV2 = (): StatusField => {
  const { data: config } = useAccountConfigQuery();

  const field = useMemo(() => {
    const sf = config?.fields?.find(
      (f) =>
        f.fieldName === STATUS_FIELD_NAME &&
        f?.objectType === FieldSectionType.Task,
    ) as StatusField;

    return {
      ...sf,
      type: FieldType.SingleSelect,
      sourceType: SourceType.Task,
    } as StatusField;
  }, [config]);

  return field;
};

/**
 *
 * @deprecated Use `useStatusFieldV2` instead
 *
 */
export const useStatusField = (): StatusField => {
  return useStatusFieldV2() as StatusField;
};

export const useStatusFieldValue = (
  value: number,
): FieldValue<FieldValueType> => {
  const status = useStatusField();

  const statusField = (status?.metaData?.options ?? []).find(
    (o) => o.value === value,
  );

  return {
    ...status,
    fieldValue: value,
    metaFieldValue: statusField,
  } as FieldValue<FieldValueType>;
};

export const useTaskFieldByName = (fieldName: string): Field => {
  const field = useTaskField(fieldName);

  if (!field) {
    throw new Error('Task Field is missing ');
  }

  return field;
};

export const useSelectedStatusOption = (
  fields?: FieldValue<FieldValueType>[],
): StatusFieldOption | undefined => {
  const status = useStatusField();

  if (!fields || !status) {
    return undefined;
  }
  const field = fields.find(
    (f) => f?.fieldId === status?.fieldId,
  ) as FieldValue<number>;
  const selectedOption =
    field &&
    status?.metaData?.options?.find((o) => o.value === field.fieldValue);

  return selectedOption && (selectedOption as StatusFieldOption);
};

export const useProjectPhaseField = (): Field | undefined => {
  const { data: config } = useAccountConfigQuery();

  const field = useMemo(
    () =>
      config?.fields?.find(
        (f) =>
          f.fieldName === STATUS_FIELD_NAME &&
          f?.objectType === FieldSectionType.ProjectPhase,
      ) as StatusField,
    [config],
  );

  return field;
};

export const useProjectStatusField = (): Field | undefined => {
  const { data: config } = useAccountConfigQuery();

  const field = useMemo(
    () =>
      config?.fields?.find(
        (f) =>
          f.fieldName === STATUS_FIELD_NAME &&
          f?.objectType === FieldSectionType.Project,
      ) as StatusField,
    [config],
  );

  return field;
};

export const useProjectPhaseStatusField = (): StatusField => {
  return useProjectPhaseField() as StatusField;
};

type SprintStatustype = StatusField & {
  type: FieldType;
  sourceType: SourceType;
};

export const useSprintStatusField = (): SprintStatustype | undefined => {
  const { data: sprintfields } = useSprintFieldsQuery();

  return useMemo(() => {
    const statusField = sprintfields?.find(
      (field) => field.fieldName === 'Status',
    );

    if (!statusField) {
      return undefined;
    }

    return {
      ...statusField,
      type: FieldType.SingleSelect,
      sourceType: SourceType.Sprint,
      fieldName: camelCase(statusField.fieldName),
    } as SprintStatustype;
  }, [sprintfields]);
};

type EpicStatusType = StatusField & {
  type: FieldType;
  sourceType: SourceType;
};

export const useEpicStatusField = (): EpicStatusType | undefined => {
  const { data: epicFields } = useEpicFieldsQuery();

  return useMemo(() => {
    const statusField = epicFields?.find(
      (field) => field.fieldName === 'Status',
    );

    if (!statusField) {
      return undefined;
    }

    return {
      ...statusField,
      type: FieldType.SingleSelect,
      sourceType: SourceType.Epic,
      fieldName: camelCase(statusField.fieldName),
    } as EpicStatusType;
  }, [epicFields]);
};
